import { DATE_FORMAT } from '@/constants';
import { Dayts } from '@/utils';
import dayjs from 'dayjs';

export const cleanDateRegExp = new RegExp(`[^0-9 /-]`, 'g');

const SEPARATORS = ['-', '/', ' '] as const;

const formatVariants = (format: string, { twoDigitYear = false }: { twoDigitYear?: boolean } = {}): string[] => {
  const base = twoDigitYear ? format.replace('YYYY', 'YY') : format;
  return SEPARATORS.map((separator: string): string => base.replace(/[-/]/g, separator));
};

// Strictly parses typed text (digits and separators only) against `format`.
export const parseTypedDate = (
  value: string = '',
  format: string = DATE_FORMAT,
  { twoDigitYear = false }: { twoDigitYear?: boolean } = {}
): Dayts | undefined => {
  const cleaned = value.trim().replace(cleanDateRegExp, '');
  if (!cleaned) return undefined;
  const candidates = [...formatVariants(format), ...(twoDigitYear ? formatVariants(format, { twoDigitYear: true }) : [])];
  return candidates
    .map((candidate: string): Dayts => new Dayts(dayjs(cleaned, candidate, true)))
    .find((date: Dayts): boolean => date.isValid());
};

// Checks for full date (4 digit year), 2 digit year leniency is reserved for blur/commit time - see isValidTypedDate.
export const isCompleteDate = (value: string = '', format: string = DATE_FORMAT): boolean => !!parseTypedDate(value, format);

// Blur/commit-time check: looser than isCompleteDate because a 2-digit year can finish a typed date
export const isValidTypedDate = (value: string = '', format: string = DATE_FORMAT): boolean =>
  !!parseTypedDate(value, format, { twoDigitYear: true });

export const formatDate =
  (format: string = DATE_FORMAT): ((value: string) => string) =>
  (value: string = ''): string => {
    if (value === '') return '';
    const typed = parseTypedDate(value, format, { twoDigitYear: true });
    if (typed) return typed.format(format);
    const stored = new Dayts(value.trim().replace(cleanDateRegExp, ''));
    return stored.isValid() ? stored.format(format) : '';
  };

// What to show in the field: a complete/valid date is formatted; a half-typed one is left as the
// user typed it (digits and separators only)
export const toDateDisplay =
  (format: string = DATE_FORMAT): ((value?: string) => string) =>
  (value: string = ''): string => {
    if (!value) return '';
    if (isCompleteDate(value, format)) return formatDate(format)(value);
    if (!/^[0-9 /-]*$/.test(value)) return formatDate(format)(value);
    return value.replace(cleanDateRegExp, '');
  };
